/**
 * Pure validation functions extracted from test-form.js.
 * None of these depend on React or component state.
 */

/**
 * Validates an Israeli ID number using the Luhn-like algorithm.
 * @param {string|number} idValue - The ID number to validate
 * @returns {boolean}
 */
export function validateIsraeliId(idValue) {
  const str = String(idValue);
  let IDnum = str;

  if (IDnum.length > 9 || IDnum.length < 5) return false;
  if (isNaN(IDnum)) return false;

  while (IDnum.length < 9) {
    IDnum = "0" + IDnum;
  }

  let mone = 0;
  for (let i = 0; i < 9; i++) {
    let incNum = Number(IDnum.charAt(i));
    incNum *= (i % 2) + 1;
    if (incNum > 9) incNum -= 9;
    mone += incNum;
  }
  return mone % 10 === 0;
}

/**
 * Checks if a phone number appears more than once in the list.
 * @param {string} phoneValue - The phone number to check
 * @param {string[]} allPhones - Array of all phone numbers in the form
 * @returns {boolean} true if no duplicates
 */
export function validatePhoneDuplicates(phoneValue, allPhones) {
  let count = 0;
  for (let i = 0; i < allPhones.length; i++) {
    if (allPhones[i] === phoneValue) {
      count++;
    }
  }
  return count <= 1;
}

/**
 * Validates file size against a maximum (in MB).
 * @param {File[]} files - Array of files from the uploader
 * @param {string} validateStr - Validation string, e.g. "img|6" (max 6MB)
 * @returns {boolean}
 */
export function validateFileSize(files, validateStr) {
  let maxSizeMB = 4;
  if (validateStr.includes("|")) {
    maxSizeMB = parseInt(validateStr.split("|")[1]);
  }

  if (files.length > 0) {
    const size = files[0].size;
    const maxFileSize = maxSizeMB * 1024 * 1024;
    if (size > maxFileSize) return false;
  }
  return true;
}

/**
 * Validates a comma-separated list of email addresses.
 * @param {string} emailString - Comma-separated emails
 * @returns {boolean}
 */
export function validateEmails(emailString) {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emails = emailString.split(',').map(email => email.trim());
  for (const email of emails) {
    if (!emailPattern.test(email)) {
      return false;
    }
  }
  return true;
}

/**
 * Builds DevExtreme validation rules array for a field definition.
 * @param {object} dataField - Field definition from template
 * @param {string} title - Tab/section title for error messages
 * @param {object} options - Context needed for validation callbacks
 * @param {Function} options.evaluateVisibility - Visibility eval function
 * @param {{ current: object }} options.docRef - Form data ref
 * @param {string[]} options.phonesNumbers - Current phone numbers state
 * @returns {object[]} DevExtreme validation rules array
 */
export function buildValidationRules(dataField, title, options) {
  const { evaluateVisibility, docRef, phonesNumbers } = options;
  const validationArray = [];

  if (dataField.validation.includes("email")) {
    validationArray.push({
      type: "email",
      message: "יש להזין אי מייל תקין" + ` (${title})`,
    });
  }

  if (dataField.validation.includes("numeric") || dataField.originalType === "dxNumberBox") {
    validationArray.push({
      type: "numeric",
      message: "יש להזין מספרים בלבד" + ` (${title})`,
    });
  }

  if (dataField.validation.includes("range")) {
    validationArray.push({
      type: "range",
      message: "חובה למלא שדה " + dataField.caption + ` (${title})`,
    });
  }

  if (dataField.validation.includes("stringLength")) {
    const validationObj = {
      type: "stringLength",
      message: dataField.validationMessage + ` (${title})`,
    };

    if (dataField.minValue === null || dataField.minValue === "" || dataField.minValue === " " || dataField.minValue === undefined) {
      validationObj.min = 0;
    } else {
      validationObj.min = dataField.minValue;
    }

    if (dataField.maxValue === null || dataField.maxValue === "" || dataField.maxValue === " " || dataField.maxValue === undefined) {
      validationObj.max = 1000000;
    } else {
      validationObj.max = dataField.maxValue;
    }

    validationArray.push(validationObj);
  }

  if (dataField.validation.includes("phone")) {
    validationArray.push({
      type: "custom",
      validationCallback: (e) => {
        if (docRef.current?.skipRequired && (!e.value || e.value.trim() === '')) {
          return true;
        }
        return validatePhoneDuplicates(e.value, phonesNumbers);
      },
      message: "אין לעשות שימוש חוזר במספר טלפון שכבר הוקלד בטופס" + ` (${title})`,
    });
  }

  if (dataField.validation.includes("id")) {
    validationArray.push({
      type: "custom",
      validationCallback: (e) => {
        if (docRef.current?.skipRequired && (!e.value || e.value.trim() === '')) {
          return true;
        }
        return validateIsraeliId(e.value);
      },
      message: " יש להזין ת.ז תקין" + ` (${title})`,
    });
  }

  if (dataField.validation.includes("compare")) {
    validationArray.push({
      type: "compare",
      message: "חובה למלא שדה " + dataField.caption + ` (${title})`,
    });
  }

  if (dataField.validation.includes("pattern")) {
    validationArray.push({
      type: "pattern",
      pattern: dataField.validationPatameters,
      message: dataField.validationMessage + ` (${title})`,
    });
  }

  if (dataField.validation.includes("img")) {
    validationArray.push({
      type: "custom",
      validationCallback: (e) => {
        if (docRef.current?.skipRequired && (!e.value || e.value.length === 0)) {
          return true;
        }
        return validateFileSize(e.value, dataField.validation);
      },
      message: "הקובץ שהעלית חורג מהגודל המבוקש" + ` (${title})`,
    });
  }

  if (dataField.validation.includes("multipleEmails")) {
    validationArray.push({
      type: "custom",
      validationCallback: (e) => {
        if (docRef.current?.skipRequired && (!e.value || e.value.trim() === '')) {
          return true;
        }
        return validateEmails(e.value);
      },
      message: dataField.validationMessage + ` (${title})`,
    });
  }

  // Required validation — skip if skipRequired is set on the document
  const shouldSkipRequired = docRef.current?.skipRequired ?? false;
  if (evaluateVisibility(dataField.required) && !shouldSkipRequired) {
    validationArray.push({
      type: "required",
      message: "חובה למלא שדה " + dataField.caption + ` (${title})`,
    });
  }

  return validationArray;
}
